/**
 * The timezone names a location can be given, in the spelling the API stores.
 *
 * The backend canonicalizes every location timezone before saving it
 * (`backend/src/utils/timezone-aliases.ts`, which explains why): a legacy alias such as
 * `Iceland` stops resolving the day the database's tzdata drops the backward links. It
 * resolves the name with `Intl`, then corrects the names ICU still spells the pre-rename
 * way. This module does the same, so the location dialog selects the zone a save will store
 * instead of showing nothing for a stored alias. `timezone-aliases.frontend.spec.ts` in the
 * backend keeps the table below equal to the backend's (the frontend CI stage has no backend).
 */

/** Names ICU reports as canonical that IANA (and so PostgreSQL) has since renamed. */
export const ICU_TO_IANA: Readonly<Record<string, string>> = Object.freeze({
    'Africa/Asmera': 'Africa/Asmara',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'America/Catamarca': 'America/Argentina/Catamarca',
    'America/Cordoba': 'America/Argentina/Cordoba',
    'America/Godthab': 'America/Nuuk',
    'America/Indianapolis': 'America/Indiana/Indianapolis',
    'America/Jujuy': 'America/Argentina/Jujuy',
    'America/Louisville': 'America/Kentucky/Louisville',
    'America/Mendoza': 'America/Argentina/Mendoza',
    'Asia/Calcutta': 'Asia/Kolkata',
    'Asia/Katmandu': 'Asia/Kathmandu',
    'Asia/Rangoon': 'Asia/Yangon',
    'Asia/Saigon': 'Asia/Ho_Chi_Minh',
    'Atlantic/Faeroe': 'Atlantic/Faroe',
    'Europe/Kiev': 'Europe/Kyiv',
    'Pacific/Enderbury': 'Pacific/Kanton',
    'Pacific/Ponape': 'Pacific/Pohnpei',
    'Pacific/Truk': 'Pacific/Chuuk',
});

/** The canonical name the API stores for `timezone`, or undefined for a name nothing knows. */
export function canonicalTimezoneName(timezone: string): string | undefined {
    const trimmed = timezone.trim();
    if (trimmed === '') return undefined;
    let icuCanonical: string;
    try {
        // resolvedOptions() names the canonical zone of an alias; an unknown name throws.
        icuCanonical = new Intl.DateTimeFormat('en', { timeZone: trimmed }).resolvedOptions()
            .timeZone;
    } catch {
        return undefined;
    }
    return ICU_TO_IANA[icuCanonical] ?? icuCanonical;
}

/**
 * The zones offered where the browser cannot list its own: `Intl.supportedValuesOf` is missing
 * from Chrome and Edge 97 and 98, which the build still targets (`vite.config.js`). These are the
 * zones OptoScale sites are in, plus UTC. A stored zone outside it is still shown (TimezoneField
 * adds it), so an edit keeps it; picking another zone there needs a newer browser.
 */
const FALLBACK_TIMEZONES: readonly string[] = [
    'America/Halifax',
    'America/Punta_Arenas',
    'America/Santiago',
    'America/St_Johns',
    'America/Vancouver',
    'Atlantic/Faroe',
    'Atlantic/Reykjavik',
    'Australia/Hobart',
    'Europe/Dublin',
    'Europe/London',
    'Europe/Oslo',
    'Pacific/Auckland',
    'UTC',
];

let timezoneNamesCache: readonly string[] | undefined;

/**
 * Every zone the runtime knows, once each, in the stored spelling and in name order.
 * `Intl.supportedValuesOf` lists canonical zones only, so no legacy alias is offered.
 *
 * Built on first use, not when the module loads: the module is imported along the routes, and
 * a throw at load time would leave a browser without `supportedValuesOf` with a blank portal.
 */
export function timezoneNames(): readonly string[] {
    if (timezoneNamesCache === undefined) {
        const zones =
            typeof Intl.supportedValuesOf === 'function'
                ? Intl.supportedValuesOf('timeZone')
                : FALLBACK_TIMEZONES;
        timezoneNamesCache = Object.freeze(
            [...new Set(zones.map((zone) => ICU_TO_IANA[zone] ?? zone))].sort()
        );
    }
    return timezoneNamesCache;
}
